
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Frequently Asked Questions | CareerGuideKerala</title>
        <meta name="description" content="Find answers to common questions about our career assessment test and services." />
      </Helmet>
      
      <Navbar />
      
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-brand-blue sm:text-4xl">Frequently Asked Questions</h1>
            <p className="mt-3 text-lg text-gray-600">
              Find answers to commonly asked questions about CareerGuideKerala
            </p>
          </div>
          
          <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  What is CareerGuideKerala.in?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    CareerGuideKerala.in is a platform designed to help students in Kerala make informed career decisions after completing 10th or 12th grade. 
                    We offer a unique career assessment test based on John L. Holland's theory, which evaluates your interests, skills, and aspirations to suggest 
                    suitable career paths, courses, and potential side hustles.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  How should I attend the assessment test?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p className="mb-4">
                    This is a crucial assessment to help guide your future career choices. The results depend entirely on the answers you provide. 
                    Please follow these simple tips:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Use a desktop or laptop for the best experience</li>
                    <li>Open the test in a new browser window</li>
                    <li>Stay focused — it takes about 20–30 minutes</li>
                    <li>Sit in a quiet place and answer on your own</li>
                    <li>Do not refresh the page once you've started</li>
                    <li>If you see a "Verifying your session" message or a blank screen, close the browser, reopen it, then log in again</li>
                  </ul>
                  <p className="mt-4 text-brand-blue font-medium">✨ Good luck with your test!</p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  Who can take this assessment?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    Any student in Kerala who has completed or is currently studying in 10th or 12th grade can take this assessment. 
                    It's also suitable for students exploring higher education, skill development, or entrepreneurship opportunities.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  Is the test available in Malayalam?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    Yes! Each assessment question is presented in both Malayalam and English for better understanding and ease of participation.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  Is this test free?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    No, the test is available at a minimal cost, exclusively for students in Kerala. 
                    It costs less than buying a shirt — yet it can help you discover a life-changing career path. 
                    Package details and pricing are available on the website.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  Can parents or teachers take the test on behalf of students?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    No. The assessment is accurate only when answered by the student independently and truthfully. 
                    Parents and teachers can support students after they receive the results, but should not assist during the test.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  What happens after I complete the test?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    Once you complete the test, you will receive a personalized career guidance report instantly. 
                    It will include recommended career paths, relevant courses, side hustles, internship suggestions, 
                    and even potential entrepreneurial options — all tailored to your strengths and interests.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-8" className="border-b border-gray-200">
                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-brand-blue">
                  How can I reset my password?
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 py-4">
                  <p>
                    For security reasons and to protect our system from automated attacks, we currently do not offer a "Forgot Password" option on the website.
                  </p>
                  <p className="mt-3">
                    However, if you wish to reset your password, simply send an email to careerguidekerala@gmail.com from your registered email ID.
                    Our support team will verify your identity and reset your password within 12 hours. You'll receive a confirmation reply once it's done.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default FAQ;
